import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Search, RefreshCcw, Trophy, Users, Gift, CheckCircle2, Plus, Minus, RotateCcw as ResetIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const STATUS_TABS = [
  { k: "all", l: "All" },
  { k: "active", l: "Active" },
  { k: "completed", l: "Completed (pending)" },
  { k: "redeemed", l: "Redeemed" },
];

export default function AdminLoyalty() {
  const [config, setConfig] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: cfg }, { data: a }] = await Promise.all([
        api.get("/admin/loyalty/config"),
        api.get("/admin/loyalty/analytics"),
      ]);
      setConfig(cfg); setAnalytics(a);
      await loadCycles(statusFilter, q);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadCycles = async (sf, query) => {
    try {
      const params = new URLSearchParams();
      if (sf && sf !== "all") params.set("status_filter", sf);
      if (query) params.set("q", query);
      const { data } = await api.get(`/admin/loyalty/cycles?${params.toString()}`);
      setCycles(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadCycles(statusFilter, q); /* eslint-disable-next-line */ }, [statusFilter]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/admin/loyalty/config", {
        purchases_required: parseInt(config.purchases_required) || 6,
        reward_type: config.reward_type || "popsicle",
        reward_label: config.reward_label || "1 Free Popsicle",
        reward_description: config.reward_description || "",
        is_active: !!config.is_active,
        is_visible: !!config.is_visible,
        reward_expiry_days: parseInt(config.reward_expiry_days) || 60,
        eligible_notes: config.eligible_notes || "",
      });
      toast.success("Loyalty config saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const adjust = async (c, delta) => {
    try {
      await api.post("/admin/loyalty/adjust", { member_id: c.member_id, delta });
      toast.success(delta > 0 ? "+1 progress" : "-1 progress");
      loadCycles(statusFilter, q);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetCycle = async (c) => {
    if (!window.confirm(`Reset the current journey for this member? (Cycle #${c.cycle_number})`)) return;
    try { await api.post("/admin/loyalty/reset", { member_id: c.member_id }); toast.success("Reset"); loadCycles(statusFilter, q); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const issue = async (c) => {
    if (!window.confirm("Manually issue a free reward for this member?")) return;
    try { await api.post("/admin/loyalty/issue-reward", { member_id: c.member_id }); toast.success("Reward issued"); loadCycles(statusFilter, q); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const redeem = async (c) => {
    if (!window.confirm(`Mark cycle #${c.cycle_number} as redeemed?`)) return;
    try { await api.post("/admin/loyalty/redeem", { cycle_id: c.id }); toast.success("Redeemed"); loadCycles(statusFilter, q); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!config) return <div>Loading…</div>;

  return (
    <div data-testid="admin-loyalty-page">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Loyalty</div>
          <h1 className="editorial-h1 text-4xl">Popsicle Loyalty Journey</h1>
        </div>
      </header>

      {/* Analytics strip */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <Stat icon={Trophy} label="Total Cycles" value={analytics.total_cycles} />
          <Stat icon={RefreshCcw} label="Active" value={analytics.active_cycles} />
          <Stat icon={Gift} label="Pending Reward" value={analytics.completed_pending_redeem} accent />
          <Stat icon={CheckCircle2} label="Redeemed" value={analytics.redeemed} />
          <Stat icon={Users} label="Participants" value={analytics.unique_participants} />
          <Stat icon={RefreshCcw} label="Avg Progress" value={analytics.avg_progress_active} />
        </div>
      )}

      {/* Config */}
      <div className="rounded-2xl bg-white border border-brand-line p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Program Configuration</div>
          <button onClick={saveConfig} disabled={saving} className="btn-pill btn-dark text-xs disabled:opacity-50" data-testid="loyalty-save-config"><Save size={12} /> {saving ? "Saving…" : "Save"}</button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Purchases required</label><input type="number" min="1" value={config.purchases_required} onChange={(e) => setConfig({ ...config, purchases_required: e.target.value })} className="pp-input" data-testid="loyalty-purchases-required" /></div>
          <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reward type</label>
            <select value={config.reward_type} onChange={(e) => setConfig({ ...config, reward_type: e.target.value })} className="pp-input" data-testid="loyalty-reward-type">
              <option value="popsicle">Popsicle</option>
              <option value="popcorn">Popcorn</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reward label</label><input value={config.reward_label} onChange={(e) => setConfig({ ...config, reward_label: e.target.value })} className="pp-input" data-testid="loyalty-reward-label" /></div>
          <div><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reward expiry (days)</label><input type="number" min="1" value={config.reward_expiry_days} onChange={(e) => setConfig({ ...config, reward_expiry_days: e.target.value })} className="pp-input" data-testid="loyalty-expiry-days" /></div>
        </div>
        <div className="mt-4"><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Reward description</label><textarea rows={2} value={config.reward_description} onChange={(e) => setConfig({ ...config, reward_description: e.target.value })} className="pp-input" /></div>
        <div className="mt-4"><label className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Eligibility notes</label><input value={config.eligible_notes || ""} onChange={(e) => setConfig({ ...config, eligible_notes: e.target.value })} className="pp-input" placeholder="e.g. Only popsicle SKUs count" /></div>
        <div className="mt-4 flex gap-5">
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!config.is_active} onChange={(e) => setConfig({ ...config, is_active: e.target.checked })} data-testid="loyalty-is-active" /> Program active</label>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!config.is_visible} onChange={(e) => setConfig({ ...config, is_visible: e.target.checked })} data-testid="loyalty-is-visible" /> Show on homepage</label>
        </div>
      </div>

      {/* Cycles list */}
      <div className="rounded-2xl bg-white border border-brand-line p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex gap-2 flex-wrap">
            {STATUS_TABS.map((t) => (
              <button key={t.k} onClick={() => setStatusFilter(t.k)} data-testid={`loyalty-tab-${t.k}`} className={`btn-pill text-xs ${statusFilter === t.k ? "btn-dark" : "btn-ghost"}`}>{t.l}</button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/45" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadCycles(statusFilter, q)} className="pp-input pl-8" placeholder="Search name or member code" data-testid="loyalty-search" />
            </div>
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">
            <tr>
              <th className="text-left py-2">Member</th>
              <th className="text-left py-2">Cycle</th>
              <th className="text-left py-2">Progress</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Reward code</th>
              <th className="text-right py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cycles.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-brand-charcoal/45">No cycles.</td></tr>}
            {cycles.map((c) => (
              <tr key={c.id} className="border-t border-brand-line" data-testid={`loyalty-cycle-${c.id}`}>
                <td className="py-2 font-mono text-xs">{c.member_id?.slice(0, 8)}…</td>
                <td className="py-2">#{c.cycle_number}</td>
                <td className="py-2">
                  <div className="flex items-center gap-2 min-w-[140px]">
                    <div className="flex-1 h-1.5 rounded-full bg-brand-muted overflow-hidden">
                      <div className="h-full bg-brand-yellow" style={{ width: `${Math.min(100, (c.progress / c.target) * 100)}%` }} />
                    </div>
                    <div className="text-xs font-mono">{c.progress}/{c.target}</div>
                  </div>
                </td>
                <td className="py-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${c.status === "redeemed" ? "bg-emerald-100 text-emerald-700" : c.status === "completed" ? "bg-brand-yellow/25 text-brand-charcoal" : c.status === "reset" ? "bg-slate-200 text-slate-600" : "bg-blue-100 text-blue-700"}`}>{c.status}</span>
                </td>
                <td className="py-2 font-mono text-xs text-brand-charcoal/70">{c.reward_code || "—"}</td>
                <td className="py-2 text-right">
                  {c.status === "active" && (
                    <>
                      <button onClick={() => adjust(c, +1)} className="text-xs mr-2 inline-flex items-center gap-1 text-brand-charcoal/70 hover:text-brand-charcoal" data-testid={`loyalty-plus-${c.id}`}><Plus size={11} /> +1</button>
                      <button onClick={() => adjust(c, -1)} className="text-xs mr-2 inline-flex items-center gap-1 text-brand-charcoal/70 hover:text-brand-charcoal" data-testid={`loyalty-minus-${c.id}`}><Minus size={11} /> -1</button>
                      <button onClick={() => issue(c)} className="text-xs mr-2 inline-flex items-center gap-1 text-emerald-700" data-testid={`loyalty-issue-${c.id}`}><Gift size={11} /> Issue</button>
                      <button onClick={() => resetCycle(c)} className="text-xs inline-flex items-center gap-1 text-red-600" data-testid={`loyalty-reset-${c.id}`}><ResetIcon size={11} /> Reset</button>
                    </>
                  )}
                  {c.status === "completed" && (
                    <button onClick={() => redeem(c)} className="btn-pill btn-dark text-xs" data-testid={`loyalty-redeem-${c.id}`}><CheckCircle2 size={11} /> Redeem</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-brand-charcoal text-brand-cream border-brand-charcoal" : "bg-white border-brand-line"}`}>
      <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${accent ? "text-brand-yellow" : "text-brand-charcoal/55"}`}><Icon size={11} /> {label}</div>
      <div className="text-2xl font-display mt-1">{value ?? 0}</div>
    </div>
  );
}
